import { HTMLAttributes, forwardRef, ReactNode } from 'react';

import { cn } from '../utils/cn';

export interface StatsCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: ReactNode;
  loading?: boolean;
  variant?: 'default' | 'gradient' | 'outlined';
  accentColor?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

export const StatsCard = forwardRef<HTMLDivElement, StatsCardProps>(
  (
    {
      className,
      title,
      value,
      change,
      changeType = 'neutral',
      icon,
      loading = false,
      variant = 'default',
      accentColor = 'primary',
      ...props
    },
    ref
  ) => {
    const accentColors = {
      primary: {
        bg: 'bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.15)]',
        text: 'text-[color:var(--ids-brand,#3B82F6)]',
        border: 'border-[color:rgba(var(--ids-brand-rgb,59,130,246),0.30)]',
        gradient: 'from-blue-500 to-blue-600',
      },
      success: {
        bg: 'bg-[color:rgba(var(--ids-success-rgb,34,197,94),0.15)]',
        text: 'text-[color:var(--ids-success,#22C55E)]',
        border: 'border-[color:rgba(var(--ids-success-rgb,34,197,94),0.30)]',
        gradient: 'from-green-500 to-green-600',
      },
      warning: {
        bg: 'bg-[color:rgba(var(--ids-warning-rgb,245,158,11),0.15)]',
        text: 'text-[color:var(--ids-warning,#F59E0B)]',
        border: 'border-[color:rgba(var(--ids-warning-rgb,245,158,11),0.30)]',
        gradient: 'from-amber-500 to-amber-600',
      },
      danger: {
        bg: 'bg-[color:rgba(var(--ids-danger-rgb,239,68,68),0.15)]',
        text: 'text-[color:var(--ids-danger,#EF4444)]',
        border: 'border-[color:rgba(var(--ids-danger-rgb,239,68,68),0.30)]',
        gradient: 'from-red-500 to-red-600',
      },
      info: {
        bg: 'bg-[color:rgba(6,182,212,0.15)]',
        text: 'text-[color:var(--ids-grad-a,#22D3EE)]',
        border: 'border-[color:rgba(6,182,212,0.30)]',
        gradient: 'from-cyan-500 to-cyan-600',
      },
    };

    const colors = accentColors[accentColor];

    return (
      <div
        ref={ref}
        className={cn(
          'relative overflow-hidden rounded-[var(--ids-radius-xl,20px)] p-5 transition-all duration-300',
          {
            // Variants
            'bg-[color:var(--ids-surface,#141B2D)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))] shadow-[var(--ids-elev-1,0_1px_2px_rgba(0,0,0,0.4))] hover:border-[color:var(--ids-border-strong,rgba(255,255,255,0.14))]':
              variant === 'default',
            [`bg-gradient-to-br ${colors.gradient} text-aurora-on-accent`]: variant === 'gradient',
            [`bg-transparent border-2 ${colors.border}`]: variant === 'outlined',
          },
          className
        )}
        {...props}
      >
        {/* Top section with icon and change */}
        <div className="flex items-center justify-between mb-4">
          {icon && (
            <div
              className={cn(
                'p-2 rounded-[var(--ids-radius-md,10px)]',
                variant === 'gradient' ? 'bg-white/20' : colors.bg
              )}
            >
              <span className={variant === 'gradient' ? 'text-aurora-on-accent' : colors.text}>
                {icon}
              </span>
            </div>
          )}
          {change && (
            <span
              className={cn('text-sm font-bold tabular-nums', {
                'text-[color:var(--ids-success,#22C55E)]': changeType === 'positive',
                'text-[color:var(--ids-danger,#EF4444)]': changeType === 'negative',
                'text-[color:var(--ids-text-muted,#94A3B8)]': changeType === 'neutral',
                'text-aurora-on-accent/80': variant === 'gradient',
              })}
            >
              {change}
            </span>
          )}
        </div>

        {/* Value */}
        {loading ? (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 border-2 border-[color:var(--ids-text-subtle,#7E8DA6)] border-t-transparent rounded-full animate-spin" />
            <span className="text-[color:var(--ids-text-muted,#94A3B8)]">...</span>
          </div>
        ) : (
          <p
            className={cn('font-mono tabular-nums text-3xl font-bold tracking-tight mb-1', {
              'text-[color:var(--ids-text,#F8FAFC)]': variant !== 'gradient',
            })}
          >
            {value}
          </p>
        )}

        {/* Title */}
        <p
          className={cn('text-[11px] font-semibold uppercase tracking-[0.07em]', {
            'text-[color:var(--ids-text-subtle,#7E8DA6)]': variant !== 'gradient',
            'text-aurora-on-accent/80': variant === 'gradient',
          })}
        >
          {title}
        </p>

        {/* Decorative accent */}
        {variant === 'default' && (
          <div
            className={cn(
              'absolute top-0 left-0 right-0 h-1 bg-gradient-to-r opacity-0 transition-opacity duration-300',
              colors.gradient,
              'group-hover:opacity-100'
            )}
          />
        )}
      </div>
    );
  }
);

StatsCard.displayName = 'StatsCard';
